/**
 * Loads everything the Brand Updates 2026 home page renders from.
 *
 * Extracted from `routes/authenticated/dashboard` so the two surfaces that show
 * this page — the beta branch of `/dashboard` (inside the existing app chrome)
 * and `/beta/home-preview` (the full-page version with the 2026 sidebar) —
 * fetch it identically. The field mapping that turns this into view data lives
 * in `utils/beta/home-model.ts`; this module only does the fetching.
 */
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  ContributionActionItem,
  ContributionHistoryData,
  ContributionPaymentRecord,
  LivePslfData,
  LiveSection127Info,
  LiveTasData,
} from 'tio-employee/services/reimagined-data';
import type { EmployerContributions } from 'tio-employee/services/reimagined-data';
import type { BetaContributionActivity } from 'tio-employee/utils/beta/home-model';

/** The per-benefit reads the home page's hero, attention rows and benefit cards are built from. */
export interface BetaHomeSnapshot {
  employerContributions: { contributions: EmployerContributions; isLiveData: boolean };
  pslfData: LivePslfData;
  tasData: LiveTasData;
  section127Info: LiveSection127Info;
}

/**
 * Everything the beta home page (`components/beta/home`) renders from.
 *
 * `reimagined` is the shared snapshot bundle; the other fields cover figures
 * the 2026 design asks for that the bundle does not carry.
 */
export interface BetaHomeRouteModel {
  reimagined: BetaHomeSnapshot;
  /** Server-authored loan-repayment action items for "Needs Your Attention". */
  actionItems: ContributionActionItem[];
  /** Tuition assistance used this benefit year; null when unresolvable. */
  tuitionYearToDate: number | null;
  /** The last / this / next contribution story; null when the history could not be loaded. */
  contributionActivity: BetaContributionActivity | null;
  /** Secure Your Future payments waiting on certification this plan year; null when unknown. */
  syfPaymentsToCertify?: number | null;
}

const SENT_STATUSES = new Set<ContributionPaymentRecord['status']>(['sent', 'resent']);

/**
 * Reads the contribution story the Active Benefits card tells off the history:
 * the newest contribution that went out, the one the servicer returned (only
 * while nothing has gone out since), and the one being prepared.
 *
 * @param history the resolved contribution history
 * @returns the activity the home model renders
 */
export function contributionActivityFrom(
  history: ContributionHistoryData
): BetaContributionActivity {
  const past = history.payments.filter((payment) => !payment.isFuture);
  const newest = past[0] ?? null;
  return {
    lastPayment: past.find((payment) => SENT_STATUSES.has(payment.status)) ?? null,
    returnedPayment: newest?.status === 'failed' ? newest : null,
    nextPayment: history.nextPayment,
    scheduledMonthlyAmount: history.scheduledMonthlyAmount,
    pageState: history.pageState,
    accountReceivingName: history.accountReceivingName,
  };
}

export async function loadBetaHomeModel(
  reimaginedData: ReimaginedDataService
): Promise<BetaHomeRouteModel> {
  const [
    employerContributions,
    pslfData,
    tasData,
    section127Info,
    // The last two feed figures the snapshot bundle does not carry. Neither is
    // load-bearing for the page, so a failure must degrade rather than blank
    // the dashboard — see how each is unwrapped below.
    contributionHistory,
    tasUsedAmount,
    syfPaymentsToCertify,
  ] = await Promise.all([
    reimaginedData.fetchEmployerContributions(),
    reimaginedData.fetchPslfData(),
    reimaginedData.fetchTasData(),
    reimaginedData.fetchSection127Info(),
    reimaginedData.fetchContributionHistory().catch((error: unknown) => {
      console.warn('[beta home] contribution history unavailable', error);
      return null;
    }),
    reimaginedData.fetchTasUsedAmount().catch((error: unknown) => {
      console.warn('[beta home] TA year-to-date unavailable', error);
      return null;
    }),
    reimaginedData.fetchSyfPaymentsToCertify().catch(() => null),
  ]);

  const reimagined: BetaHomeSnapshot = {
    employerContributions,
    pslfData,
    tasData,
    section127Info,
  };

  /**
   * `fetchTasUsedAmount` swallows its own errors and resolves
   * `{ yearToDate: 0, isLiveData: false }`, so the `.catch()` above can never
   * turn a failed request into null on its own — a TAS/OpenSearch outage would
   * otherwise render a confident `$0` for an unknown amount.
   *
   * `isLiveData: false` also covers demo mode and a missing employee id, so
   * only the non-demo case becomes null: in a demo the fixture value IS the
   * intended number.
   */
  const tuitionYearToDate =
    tasUsedAmount && (tasUsedAmount.isLiveData || reimaginedData.isDemoMode)
      ? tasUsedAmount.yearToDate
      : null;

  return {
    reimagined,
    actionItems: contributionHistory?.actionItems ?? [],
    tuitionYearToDate,
    contributionActivity: contributionHistory
      ? contributionActivityFrom(contributionHistory)
      : null,
    syfPaymentsToCertify,
  };
}
